import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertTriangle,
  Bell,
  BellRing,
  Wrench,
  CreditCard,
  Truck,
  Users,
  ChevronRight,
  Filter,
  RefreshCw
} from "lucide-react";
import { toast } from "sonner";

const severityConfig = {
  critical: { 
    label: "Critical", 
    className: "bg-red-500/20 text-red-400 border-red-500/30",
    icon: AlertTriangle,
    color: "text-red-400"
  },
  high: { 
    label: "High", 
    className: "bg-orange-500/20 text-orange-400 border-orange-500/30",
    icon: BellRing,
    color: "text-orange-400"
  },
  medium: { 
    label: "Medium", 
    className: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
    icon: Bell,
    color: "text-yellow-400"
  },
  low: { 
    label: "Low", 
    className: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    icon: Bell,
    color: "text-blue-400"
  }
};

const typeConfig = {
  service_due: { 
    label: "Service Due", 
    icon: Wrench,
    color: "text-yellow-400"
  },
  license_expiring: { 
    label: "License Expiring", 
    icon: CreditCard,
    color: "text-orange-400"
  },
  license_expired: { 
    label: "License Expired", 
    icon: CreditCard,
    color: "text-red-400"
  }
};

export default function AlertsPage() {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [severityFilter, setSeverityFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");

  useEffect(() => {
    fetchAlerts();
  }, []);

  const fetchAlerts = async () => {
    try {
      const [alertsRes, summaryRes] = await Promise.all([
        apiClient.get("/alerts"),
        apiClient.get("/alerts/summary")
      ]);
      setAlerts(alertsRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      toast.error("Failed to fetch alerts");
      console.error("Failed to fetch alerts:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchAlerts();
  };

  const handleNavigate = (alert) => {
    if (alert.entity_type === "vehicle") {
      navigate("/vehicles");
    } else if (alert.entity_type === "driver") {
      navigate("/drivers");
    }
  };

  const filteredAlerts = alerts.filter(alert => {
    const matchesSeverity = severityFilter === "all" || alert.severity === severityFilter;
    const matchesType = typeFilter === "all" || alert.alert_type === typeFilter;
    return matchesSeverity && matchesType;
  });

  const SummaryCard = ({ title, count, icon: Icon, color, onClick }) => (
    <Card 
      className={`bg-card border-border rounded-sm ${onClick ? "cursor-pointer hover:border-primary/50 transition-colors" : ""}`}
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider">{title}</p>
            <p className={`text-2xl font-heading font-bold ${color} mt-1`}>{count}</p>
          </div>
          <div className={`w-10 h-10 rounded-sm bg-${color.replace('text-', '')}/10 border border-${color.replace('text-', '')}/30 flex items-center justify-center`}>
            <Icon className={`w-5 h-5 ${color}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="space-y-6" data-testid="alerts-loading">
        <div className="skeleton-tactical h-8 w-48 rounded" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="skeleton-tactical h-24 rounded" />
          ))}
        </div>
        <div className="skeleton-tactical h-64 w-full rounded" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="alerts-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold tracking-tight text-foreground">
            MAINTENANCE & EXPIRY ALERTS
          </h1>
          <p className="text-sm font-mono text-muted-foreground mt-1">
            Track service schedules and license expiry warnings
          </p>
        </div>
        <Button
          onClick={handleRefresh}
          disabled={refreshing}
          className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-heading tracking-wider"
          data-testid="refresh-alerts-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
          REFRESH
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <SummaryCard 
          title="Total Alerts" 
          count={summary?.total || 0}
          icon={Bell}
          color="text-primary"
        />
        <SummaryCard 
          title="Critical" 
          count={summary?.by_severity?.critical || 0}
          icon={AlertTriangle}
          color="text-red-400"
          onClick={() => setSeverityFilter("critical")}
        />
        <SummaryCard 
          title="High Priority" 
          count={summary?.by_severity?.high || 0}
          icon={BellRing}
          color="text-orange-400"
          onClick={() => setSeverityFilter("high")}
        />
        <SummaryCard 
          title="Service Due" 
          count={summary?.by_type?.service_due || 0}
          icon={Wrench}
          color="text-yellow-400"
          onClick={() => setTypeFilter("service_due")}
        />
        <SummaryCard 
          title="License Alerts" 
          count={(summary?.by_type?.license_expiring || 0) + (summary?.by_type?.license_expired || 0)}
          icon={CreditCard}
          color="text-blue-400"
          onClick={() => setTypeFilter("license_expiring")}
        />
      </div>

      {/* Filters */}
      <Card className="bg-card border-border rounded-sm">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <Select value={severityFilter} onValueChange={setSeverityFilter}>
              <SelectTrigger className="w-full sm:w-48 bg-input border-border rounded-sm font-mono text-sm" data-testid="severity-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter by severity" />
              </SelectTrigger>
              <SelectContent className="bg-card border-border rounded-sm">
                <SelectItem value="all" className="font-mono text-sm">All Severities</SelectItem>
                {Object.entries(severityConfig).map(([key, config]) => (
                  <SelectItem key={key} value={key} className="font-mono text-sm">{config.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-full sm:w-48 bg-input border-border rounded-sm font-mono text-sm" data-testid="type-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent className="bg-card border-border rounded-sm">
                <SelectItem value="all" className="font-mono text-sm">All Types</SelectItem>
                {Object.entries(typeConfig).map(([key, config]) => (
                  <SelectItem key={key} value={key} className="font-mono text-sm">{config.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {(severityFilter !== "all" || typeFilter !== "all") && (
              <Button 
                variant="outline" 
                onClick={() => { setSeverityFilter("all"); setTypeFilter("all"); }}
                className="border-border rounded-sm font-mono text-sm"
              >
                Clear Filters
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Critical Alerts Banner */}
      {summary?.by_severity?.critical > 0 && (
        <Card className="bg-red-500/10 border-red-500/30 rounded-sm" data-testid="critical-alert-banner">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className="w-6 h-6 text-red-400 animate-pulse" />
            <div>
              <p className="text-sm font-heading text-red-400 tracking-wide">CRITICAL ALERTS REQUIRE IMMEDIATE ATTENTION</p>
              <p className="text-xs font-mono text-red-400/70">
                {summary.by_severity.critical} critical alert(s) detected - expired licenses or overdue services
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Alerts Table */}
      <Card className="bg-card border-border rounded-sm" data-testid="alerts-table">
        <CardHeader className="border-b border-border pb-3">
          <CardTitle className="font-heading text-sm tracking-wide text-foreground flex items-center gap-2">
            <Bell className="w-4 h-4 text-primary" />
            ACTIVE ALERTS ({filteredAlerts.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="font-heading text-xs tracking-wide text-muted-foreground">SEVERITY</TableHead>
                  <TableHead className="font-heading text-xs tracking-wide text-muted-foreground">TYPE</TableHead>
                  <TableHead className="font-heading text-xs tracking-wide text-muted-foreground">ALERT</TableHead>
                  <TableHead className="font-heading text-xs tracking-wide text-muted-foreground">ENTITY</TableHead>
                  <TableHead className="font-heading text-xs tracking-wide text-muted-foreground">DUE DATE</TableHead>
                  <TableHead className="font-heading text-xs tracking-wide text-muted-foreground">DAYS</TableHead>
                  <TableHead className="font-heading text-xs tracking-wide text-muted-foreground text-right">ACTION</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAlerts.map((alert) => {
                  const sevConfig = severityConfig[alert.severity] || severityConfig.low;
                  const tConfig = typeConfig[alert.alert_type] || typeConfig.service_due;
                  const SevIcon = sevConfig.icon;
                  const TypeIcon = tConfig.icon;
                  const EntityIcon = alert.entity_type === "vehicle" ? Truck : Users;
                  
                  return (
                    <TableRow key={alert.id} className="border-border hover:bg-accent/50" data-testid={`alert-row-${alert.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <SevIcon className={`w-4 h-4 ${sevConfig.color}`} />
                          <Badge className={`${sevConfig.className} rounded-sm font-mono text-xs border`}>
                            {sevConfig.label}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <TypeIcon className={`w-4 h-4 ${tConfig.color}`} />
                          <span className="font-mono text-sm text-muted-foreground">{tConfig.label}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium text-foreground">{alert.title}</p>
                          <p className="text-xs font-mono text-muted-foreground">{alert.message}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-sm bg-primary/10 border border-primary/30 flex items-center justify-center">
                            <EntityIcon className="w-3.5 h-3.5 text-primary" />
                          </div>
                          <span className="font-mono text-sm text-muted-foreground">{alert.entity_name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="font-mono text-sm text-muted-foreground">
                          {alert.due_date || "-"}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className={`font-mono text-sm font-bold ${
                          alert.days_remaining !== null && alert.days_remaining < 0 
                            ? "text-red-400" 
                            : alert.days_remaining !== null && alert.days_remaining <= 7 
                              ? "text-orange-400" 
                              : "text-muted-foreground"
                        }`}>
                          {alert.days_remaining !== null 
                            ? alert.days_remaining < 0 
                              ? `${Math.abs(alert.days_remaining)} overdue`
                              : alert.days_remaining
                            : "-"}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleNavigate(alert)}
                          className="font-mono text-xs hover:bg-primary/10"
                          data-testid={`alert-action-${alert.id}`}
                        >
                          View <ChevronRight className="w-3 h-3 ml-1" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {filteredAlerts.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12">
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                          <Bell className="w-6 h-6 text-primary" />
                        </div>
                        <p className="font-heading text-foreground">No Alerts</p>
                        <p className="text-sm font-mono text-muted-foreground">
                          {alerts.length === 0 
                            ? "All vehicles and drivers are up to date!"
                            : "No alerts match your current filters"}
                        </p>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Alert Legend */}
      <Card className="bg-card border-border rounded-sm">
        <CardHeader className="border-b border-border pb-3">
          <CardTitle className="font-heading text-sm tracking-wide text-foreground">
            ALERT SEVERITY GUIDE
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {Object.entries(severityConfig).map(([key, config]) => {
              const Icon = config.icon;
              return (
                <div key={key} className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-sm bg-${config.color.replace('text-', '')}/10 flex items-center justify-center flex-shrink-0`}>
                    <Icon className={`w-4 h-4 ${config.color}`} />
                  </div>
                  <div>
                    <p className={`font-heading text-sm ${config.color}`}>{config.label}</p>
                    <p className="text-xs font-mono text-muted-foreground">
                      {key === "critical" && "Expired or overdue items requiring immediate action"}
                      {key === "high" && "Items due within 14 days"}
                      {key === "medium" && "Items due within 30 days"}
                      {key === "low" && "Items due within 90 days"}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
